import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';
import { Table, Layout, Button, Card, Upload, message, Modal, Input, Typography, Space, Menu } from 'antd';
import { 
  UploadOutlined, AudioOutlined, FileTextOutlined, SettingOutlined, HistoryOutlined, DollarOutlined, LogoutOutlined, HomeOutlined 
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { logout } from '../api/auth';  // 添加这行

const { Header, Content, Footer } = Layout;

function SubtitleManager() {
  const { t } = useTranslation();
  const [subtitles, setSubtitles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [subtitleFile, setSubtitleFile] = useState(null);
  const navigate = useNavigate();
  const [editingName, setEditingName] = useState(null);

  const fetchSubtitles = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/subtitles/list', {
        credentials: 'include',
      });
      const data = await response.json();
      if (data.success) {
        setSubtitles(data.subtitles);
      } else {
        message.error(t('subtitle.fetchFail'));
      }
    } catch (error) {
      console.error('fetch subtitle list error:', error);
      message.error(t('subtitle.fetchFail'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubtitles();
  }, []);

  const handleSubtitleUpload = async () => {
    if (!subtitleFile) {
      message.warning(t('subtitle.selectFileFirst'));
      return;
    }

    const formData = new FormData();
    formData.append('subtitle', subtitleFile);

    try {
      const response = await fetch('/api/subtitles/upload', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      const data = await response.json();
      if (data.success) {
        message.success(t('subtitle.uploadSuccess'));
        setSubtitleFile(null);
        fetchSubtitles();
      } else {
        if (data.code === 'QUOTA_EXCEEDED') {
          message.error(t('subtitle.quotaExceeded'));
        } else {
          message.error(`${t('subtitle.uploadFail')}: ${data.message || t('common.unknownError')}`);
        }
      }
    } catch (error) {
      console.error('上传错误:', error);
      message.error(t('subtitle.uploadError'));
    }
  };

  const handleDeleteSubtitle = async (id) => {
    Modal.confirm({
      title: t('common.confirmDelete'),
      content: t('subtitle.confirmDeleteMsg'),
      okText: t('common.ok'),
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          const response = await fetch(`/api/subtitles/${id}`, {
            method: 'DELETE',
            credentials: 'include',
          });
          const data = await response.json();
          if (data.success) {
            message.success(t('common.deleteSuccess'));
            fetchSubtitles();
          } else {
            message.error(t('common.deleteFail'));
          }
        } catch (error) {
          console.error('删除错误:', error);
          message.error(t('subtitle.deleteError'));
        }
      },
    });
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('退出失败:', error);
      message.error(t('common.logoutFail'));
    }
  };

  const columns = [
    {
      title: t('subtitle.filename'),
      dataIndex: 'filename',
      key: 'filename',
      render: (text, record) => (
        editingName === record._id ? (
          <Input
            defaultValue={text}
            onPressEnter={(e) => handleSubtitleRename(record._id, e.target.value)}
            onBlur={() => setEditingName(null)}
            autoFocus
          />
        ) : (
          <span 
            style={{ cursor: 'pointer' }}
            onClick={() => setEditingName(record._id)}
          >
            {text}
          </span>
        )
      )
    },
    {
      title: t('title.action'),
      key: 'action',
      render: (text, record) => (
        <span>
          <Button onClick={() => navigate(`/editor/${record._id}`)}>{t('title.dashboard')}</Button>
          <Button onClick={() => handleDeleteSubtitle(record._id)} style={{ marginLeft: 8 }}>{t('title.delete')}</Button>
        </span>
      ),
    },
  ];

  const handleSubtitleRename = async (id, newName) => {
    try {
      const response = await fetch(`/api/subtitles/${id}/rename`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ filename: newName }),
      });
      const data = await response.json();
      if (data.success) {
        message.success(t('subtitle.renameSuccess'));
        setEditingName(null);
        fetchSubtitles();
      } else {
        message.error(t('subtitle.renameFail'));
      }
    } catch (error) {
      console.error('rename subtitle filename error:', error);
      message.error(t('subtitle.renameFail'));
    }
  };

  return (
    <Layout className="layout">
      <Header style={{ background: '#001529', padding: '0 24px', display: 'flex', alignItems: 'center' }}>
        <div style={{ flex: '1', display: 'flex', alignItems: 'center' }}>
          <Space align="center">
            <Button 
              icon={<HomeOutlined />} 
              onClick={() => navigate('/dashboard')}
              style={{ 
                background: '#fff',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                height: '32px'
              }}
            >
              {t('title.back')}
            </Button>
            <Typography.Title 
              level={4} 
              style={{ 
                margin: 0,
                color: '#fff',
                lineHeight: '32px'
              }}
            >
              {t('menu.subtitle')}
            </Typography.Title>
          </Space>
        </div>
        <div style={{marginLeft:8}}><LanguageSelector/></div>
        <Menu
          theme="dark"
          mode="horizontal"
          items={[
            { key: 'audio', label: t('menu.audio'), icon: <AudioOutlined /> },
            { key: 'subtitle', label: t('menu.subtitle'), icon: <FileTextOutlined /> },
            { key: 'rules', label: t('menu.rules'), icon: <SettingOutlined /> },
            { key: 'history', label: t('menu.history'), icon: <HistoryOutlined /> },
            { key: 'plan', label: t('menu.plan'), icon: <DollarOutlined /> },
            { key: 'logout', label: t('menu.logout'), icon: <LogoutOutlined /> }
          ]}
          onClick={({ key }) => {
            // eslint-disable-next-line default-case
             switch(key) {
              case 'audio':
                navigate('/audio-manager');
                break;
              case 'subtitle':
                navigate('/subtitle-manager');
                break;
              case 'rules':
                navigate('/rule-manager');
                break;
              case 'history':
                navigate('/history');
                break;
              case 'plan':
        navigate('/plan');
        break;
              case 'logout':
                handleLogout();
                break;
            }
          }}
          style={{ background: '#001529' }}
        />
      </Header>
      <Content style={{ padding: '24px', minHeight: 'calc(100vh - 64px - 70px)' }}>
        <Card bordered={false}>
          <div>
            <Upload
              beforeUpload={(file) => {
                setSubtitleFile(file);
                return false;
              }}
              fileList={subtitleFile ? [subtitleFile] : []}
              onRemove={() => {
                setSubtitleFile(null);
                return true;
              }}
            >
              <Button icon={<UploadOutlined />}>{t('subtitle.chooseFile')}</Button>
            </Upload>
            <Button 
              type="primary" 
              onClick={handleSubtitleUpload} 
              style={{ marginTop: '10px' }}
            >
              {t('subtitle.uploadFile')}
            </Button>

            <Table
              columns={columns}
              dataSource={subtitles}
              rowKey="_id"
              loading={loading}
              style={{ marginTop: '20px' }}
            />
          </div>
        </Card>
      </Content>
      <Footer style={{ textAlign: 'center' }}>
        {t('footer.copyright')} ©{new Date().getFullYear()}
      </Footer>
    </Layout>
  );
}

export default SubtitleManager;